/**
 * B000201 JAN空番照会
 */

import { B000201Find, B000201FindRequest, catchRestError } from "clients/rest";
import {
  msgState,
  SCodeSelect,
  SFrame,
  SLabel,
  SNormalButton,
  SNumberField,
  STable,
  SText,
} from "components";
import { getSCode, Saibankensu, Saibanshubetsu, SCode } from "messages/codes";
import { labels } from "messages/labels";
import { createRef, useContext, useEffect, useState } from "react";
import { FormProvider, useForm } from "react-hook-form";
import { useNavigate } from "react-router-dom";
import { useRecoilValue, useSetRecoilState } from "recoil";
import { saibanshubetsu } from "services/codes";
import { focusState, SDialogContext, useFocus } from "states";

// 呼び出し時のプロパティ
type Props = Partial<{
  // 呼び出し元へデータを送る場合に設定する。
  handleSubmit: (data: any) => void;
}>;

// 固定値
const apjJan = "4966005";
const apjKoboJan = "4995027";
const instore = "28";
const fiveDigitStart = "00000";
const tenDigitStart = "0000000000";
const fiveDigitEnd = "99999";
const tenDigitEnd = "9999999999";

// コンポーネントで設定されるフィールドの型を定義する
type Fields = Partial<{
  saibanshubetsu: SCode;
  koteiJanCd: string;
  kensu: SCode;
  kaishiJanCd: string;
}>;

// テーブル定義
type Data = Partial<{
  gyoNo: number;
  janKouho: string;
}>;

// フィールドの初期値を設定する。
const defaultFields: Fields = {
  saibanshubetsu: getSCode(Saibanshubetsu, "0"),
  koteiJanCd: apjJan,
  kensu: getSCode(Saibankensu, "1"),
  kaishiJanCd: "",
};

// テーブルを定義する。
const columns = [
  { field: "gyoNo", title: "行", width: "3rem" },
  { field: "janKouho", title: "JANコード(空番)", width: "13rem" },
];

const focusId = "B000201";

export const B000201 = (props: Props) => {
  useFocus(focusId);
  return <Page {...props} />;
};
const Page = (props: Props) => {
  // 状態管理の初期設定
  // テーブルの値取得用Ref
  const tableRef = createRef();
  const [rows, setRows] = useState<Data[]>([]);
  // ダイアログで処理を行う関数
  const handleSubmit = useContext(SDialogContext);
  //フォーム関数
  const methods = useForm<Fields>({
    mode: "onBlur",
    defaultValues: defaultFields,
  });
  // フォーカス関数
  const { focus, register } = useRecoilValue(focusState(focusId));
  // エラー表示
  const setMsg = useSetRecoilState(msgState);
  // 画面遷移
  const navigate = useNavigate();

  // 採番種別が2:インストアかどうか
  const isInstore =
    methods.watch("saibanshubetsu")?.value === saibanshubetsu.instore;

  // 初期フォーカス
  useEffect(() => {
    focus(1);
  });

  // 検索ボタンクリックイベント
  const handleClickSearchButton = () => {
    // 検索フォームのデータを取得する。

    const fields = methods.getValues();

    // 検索パラメータを作成する。
    const param: B000201FindRequest = {
      janCdFrom: handleMakeJanCdFrom(),
      janCdTo: handleMakeJanCdTo(),
      kensu: fields.kensu?.value,
    };
    // API呼び出し
    B000201Find(param)
      // 正常取得時
      .then((value) => {
        // グリッドへのマッピング
        const gridDataList = value.data.map<Data>((value, index) => {
          // 行番号
          const gyoNo: number = index + 1;
          // 行のデータセット
          const data: Data = {
            gyoNo: Number(gyoNo),
            janKouho: value.janKouho,
          };
          return data;
        });
        // テーブルに反映
        setRows(gridDataList);
      })
      // エラー時
      .catch((reason) => {
        setRows([]);
        catchRestError(reason, navigate, setMsg);
      });
  };

  // テーブル行ダブルクリックイベント
  const handleDoubleClickTableRow = (data: Data) => {
    handleSubmit(data);
  };

  // 固定コードに開始コードを連結して検索開始番号（janCdFrom）を作成する
  const handleMakeJanCdFrom = () => {
    // フィールドの値を取得
    const fields = methods.getValues();
    let zeroPaddingKaishiJanCd = "";

    // 開始コードの桁数制限が10桁の場合
    if (fields.koteiJanCd === instore) {
      zeroPaddingKaishiJanCd = (tenDigitStart + fields.kaishiJanCd).slice(-10);
    }
    // 開始コードの桁数制限が5桁の場合
    else {
      zeroPaddingKaishiJanCd = (fiveDigitStart + fields.kaishiJanCd).slice(-5);
    }

    const janCdFrom = fields.koteiJanCd! + zeroPaddingKaishiJanCd;

    return janCdFrom;
  };

  // 固定コードに終了コードを連結して検索終了番号（janCdTo）を作成する
  const handleMakeJanCdTo = () => {
    // フィールドの値を取得
    const fields = methods.getValues();
    let janCdTo = "";

    // APJの場合
    if (fields.saibanshubetsu?.value === saibanshubetsu.apj) {
      janCdTo = apjJan + fiveDigitEnd;
    }
    // APJ工房の場合
    else if (fields.saibanshubetsu?.value === saibanshubetsu.apjKobo) {
      janCdTo = apjKoboJan + fiveDigitEnd;
    }
    // インストアの場合
    else if (fields.saibanshubetsu?.value === saibanshubetsu.instore) {
      janCdTo = instore + tenDigitEnd;
    }

    return janCdTo;
  };

  // 採番種別の切り替え時
  const handleDisplayKoteiJanCd = () => {
    // フィールドの値を取得
    const fields = methods.getValues();

    // APJの場合
    if (fields.saibanshubetsu?.value === saibanshubetsu.apj) {
      methods.setValue("kensu", getSCode(Saibankensu, "1"));
      methods.setValue("kaishiJanCd", "");
      methods.setValue("koteiJanCd", apjJan);
    }
    // APJ工房の場合
    else if (fields.saibanshubetsu?.value === saibanshubetsu.apjKobo) {
      methods.setValue("kensu", getSCode(Saibankensu, "1"));
      methods.setValue("kaishiJanCd", "");
      methods.setValue("koteiJanCd", apjKoboJan);
    }
    // インストアの場合
    else if (fields.saibanshubetsu?.value === saibanshubetsu.instore) {
      methods.setValue("kensu", getSCode(Saibankensu, "1"));
      methods.setValue("kaishiJanCd", "");
      methods.setValue("koteiJanCd", instore);
    }
  };

  // レイアウト設定
  return (
    <FormProvider {...methods}>
      <SFrame title="JAN空番照会" gridTemplateColumns="5rem 8rem 1rem 2rem">
        <SLabel>採番種別</SLabel>
        <SCodeSelect
          name="saibanshubetsu"
          options={Saibanshubetsu}
          {...register(1)}
          onChange={handleDisplayKoteiJanCd}
        />
        <SLabel gridColumn="1">固定コード</SLabel>
        <SNumberField name="koteiJanCd" code size={7} disabled />
        <SLabel gridColumn="1">採番件数</SLabel>
        <SCodeSelect name="kensu" options={Saibankensu} {...register(2)} />
        <SLabel gridColumn="1">開始コード</SLabel>
        {isInstore ? (
          <>
            <SNumberField name="kaishiJanCd" size={10} {...register(3)} />
          </>
        ) : (
          <>
            <SNumberField name="kaishiJanCd" size={5} {...register(3)} />
          </>
        )}
        <SText align="center">～</SText>
        <SNormalButton
          label={labels.buttons.search}
          gridRow={5}
          gridColumn={5}
          {...register(10)}
          onClick={handleClickSearchButton}
        />
      </SFrame>
      <SFrame>
        <STable
          keyName="gyoNo"
          data={rows}
          columns={columns}
          tableRef={tableRef}
          handleDoubleClick={handleDoubleClickTableRow}
        />
      </SFrame>
    </FormProvider>
  );
};
